import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  IconButton,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Event, BeachAccess, EventNote, Close, CalendarMonth } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { reportsAPI, settingsAPI, employeeHolidaysAPI } from '../services/api';

const Calendar = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [calendarData, setCalendarData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [weekendOff, setWeekendOff] = useState(true);
  const [firstSaturdayWork, setFirstSaturdayWork] = useState(false);
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [timezoneLoaded, setTimezoneLoaded] = useState(false);
  const [employeeHolidays, setEmployeeHolidays] = useState([]);
  const [optionalHolidays, setOptionalHolidays] = useState([]);
  const [selectedHolidayIds, setSelectedHolidayIds] = useState([]);
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [holidayLimit, setHolidayLimit] = useState(10);
  const [savingHolidays, setSavingHolidays] = useState(false);
  const [holidayMessage, setHolidayMessage] = useState({ type: '', text: '' });

  const isEmployee = user?.role === 'employee';

  // Handle URL date parameter
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const date = new Date(dateParam);
      if (!isNaN(date.getTime())) {
        setSelectedMonth(date.getMonth());
        setSelectedYear(date.getFullYear());
        setSelectedDay(dateParam);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    loadCalendar();
    loadWorkScheduleSettings();
    loadEmployeeHolidays();
    if (isEmployee) {
      loadOptionalHolidays();
    }
  }, [selectedMonth, selectedYear]);

  // Load timezone
  useEffect(() => {
    const loadTimezoneData = async () => {
      try {
        const tzResponse = await settingsAPI.getByKey('timezone');
        if (tzResponse.data.success && tzResponse.data.data) {
          setTimezone(tzResponse.data.data.value || 'Asia/Kolkata');
        }
        setTimezoneLoaded(true);
      } catch (error) {
        console.log('Error loading timezone:', error);
        setTimezoneLoaded(true);
      }
    };
    loadTimezoneData();
  }, []);

  const loadOptionalHolidays = async () => {
    try {
      const response = await employeeHolidaysAPI.getOptional({ year: selectedYear });
      if (response.data.success) {
        const { holidays: holidayList, holidayLimit: limit, approvedCount } = response.data.data;
        setOptionalHolidays(holidayList || []);
        if (limit) setHolidayLimit(limit);
        const selected = (holidayList || [])
          .filter(h => h.selectionStatus === 'pending' || h.selectionStatus === 'approved')
          .map(h => h.id);
        setSelectedHolidayIds(selected);
      }
    } catch (error) {
      console.error('Error loading optional holidays:', error);
    }
  };

  const handleSelectHoliday = (holidayId) => {
    setSelectedHolidayIds(prev => {
      if (prev.includes(holidayId)) {
        return prev.filter(id => id !== holidayId);
      } else {
        const approvedCount = optionalHolidays.filter(
          h => prev.includes(h.id) && h.selectionStatus === 'approved'
        ).length;
        const pendingCount = optionalHolidays.filter(
          h => prev.includes(h.id) && h.selectionStatus === 'pending'
        ).length;
        const currentSelected = approvedCount + pendingCount;
        
        if (currentSelected >= holidayLimit) {
          setHolidayMessage({ type: 'error', text: `You can only select up to ${holidayLimit} optional holidays` });
          setTimeout(() => setHolidayMessage({ type: '', text: '' }), 3000);
          return prev;
        }
        return [...prev, holidayId];
      }
    });
  };

  const handleSaveHolidays = async () => {
    try {
      setSavingHolidays(true);
      setHolidayMessage({ type: '', text: '' });
      const response = await employeeHolidaysAPI.select({ holidayIds: selectedHolidayIds });
      if (response.data.success) {
        setHolidayMessage({ type: 'success', text: 'Holidays saved successfully' });
        loadEmployeeHolidays();
        loadOptionalHolidays();
        setTimeout(() => setShowHolidayDialog(false), 1500);
      } else {
        setHolidayMessage({ type: 'error', text: response.data.message });
      }
    } catch (error) {
      setHolidayMessage({ type: 'error', text: 'Failed to save holidays' });
    } finally {
      setSavingHolidays(false);
    }
  };

  const loadEmployeeHolidays = async () => {
    try {
      const response = await employeeHolidaysAPI.getEmployeeApproved(user?.id, { year: selectedYear });
      if (response.data.success) {
        setEmployeeHolidays(response.data.data);
      }
    } catch (error) {
      console.error('Error loading employee holidays:', error);
    }
  };

  const formatTimeToTimezone = (utcTime) => {
    if (!utcTime) return '-';
    try {
      const date = new Date(utcTime);
      return date.toLocaleString('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return utcTime;
    }
  };

  const loadWorkScheduleSettings = async () => {
    try {
      const [weekendResponse, saturdayResponse] = await Promise.all([
        settingsAPI.getByKey('weekend_off'),
        settingsAPI.getByKey('first_saturday_work'),
      ]);
      
      if (weekendResponse.data.success && weekendResponse.data.data) {
        setWeekendOff(weekendResponse.data.data.value === 'true');
      }
      if (saturdayResponse.data.success && saturdayResponse.data.data) {
        setFirstSaturdayWork(saturdayResponse.data.data.value === 'true');
      }
    } catch (error) {
      console.log('Error loading work schedule settings:', error);
    }
  };

  const loadCalendar = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.getCalendar(selectedMonth, selectedYear);
      if (response.data.success) {
        setCalendarData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month];
  };

  const goToPrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
    setSelectedDay(null);
  };

  const goToNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
    setSelectedDay(null);
  };

  const getDayStatus = (dayData, dayStr) => {
    const empHoliday = employeeHolidays.find(h =>
      h.holiday && h.holiday.holiday_date === dayStr
    );
    if (empHoliday) {
      return {
        type: 'employee_holiday',
        label: 'My Holiday',
        color: '#D4AF37'
      };
    }
    if (dayData?.holidays && dayData.holidays.length > 0) {
      return { type: 'holiday', label: 'Holiday', color: '#D4AF37' };
    }
    if (dayData?.leaves && dayData.leaves.length > 0) {
      return { type: 'leave', label: 'Leave', color: '#D4AF37' };
    }
    if (dayData?.attendance && dayData.attendance.length > 0) {
      const present = dayData.attendance.filter(a => a.status === 'Present' || a.status === 'present').length;
      const weekend = dayData.attendance.filter(a => a.status === 'Weekend' || a.status === 'weekend').length;
      if (present > 0) {
        return { type: 'present', label: `${present} Present`, color: '#81C784' };
      }
      if (weekend > 0) {
        return { type: 'weekend', label: 'Weekend Off', color: '#9E9E9E' };
      }
      return { type: 'absent', label: 'Absent', color: '#EF9A9A' };
    }
    return { type: 'none', label: '-', color: '#D4AF37' };
  };

  const renderCalendar = () => {
    if (!calendarData || !calendarData.days) {
      return <Typography sx={{ color: '#B4C4A8', textAlign: 'center', py: 4 }}>No data available</Typography>;
    }

    const days = calendarData.days;
    const firstDay = new Date(selectedYear, selectedMonth, 1).getDay();
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();

    const emptyCells = [];
    for (let i = 0; i < firstDay; i++) {
      emptyCells.push(<Grid item xs={12/7} key={`empty-${i}`} />);
    }

    const dayCells = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = days.find(d => d.date === dateStr) || { date: dateStr, day: '', attendance: [], holidays: [], leaves: [] };
      const status = getDayStatus(dayData, dateStr);
      const dayOfWeek = new Date(dateStr).getDay();
      const isSaturday = dayOfWeek === 6;
      const isSunday = dayOfWeek === 0;
      const isWeekend = isSaturday || isSunday;
      const isFirstSaturday = isSaturday && day <= 7;
      
      let dayColor = '#FFFFFF';
      let bgColor = 'rgb(10, 25, 41)';
      
      if (weekendOff && isWeekend) {
        if (firstSaturdayWork && isFirstSaturday) {
          dayColor = '#81C784';
          bgColor = 'rgb(10, 25, 41)';
        } else if (isSunday || (isSaturday && !isFirstSaturday)) {
          dayColor = '#EF9A9A';
          bgColor = 'rgb(10, 25, 41)';
        }
      }
      
      const isSelected = selectedDay === dateStr;
      const today = new Date().toISOString().split('T')[0];
      const isToday = dateStr === today;

      dayCells.push(
        <Grid item xs={12/7} key={day}>
          <Card
            sx={{
              cursor: 'pointer',
              textAlign: 'center',
              p: { xs: 1, sm: 1.5 },
              minHeight: { xs: 70, sm: 90 },
              bgcolor: isSelected ? 'rgba(212,175,55,0.15)' : isToday ? 'rgba(76, 175, 80, 0.1)' : bgColor,
              border: isSelected ? '2px solid #D4AF37' : isToday ? '2px solid #81C784' : '1px solid rgba(212,175,55,0.3)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(212,175,55,0.08)',
                transform: 'translateY(-2px)',
              },
            }}
            onClick={() => setSelectedDay(dateStr)}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: day === 1 ? 700 : 400,
                color: dayColor,
                mb: { xs: 0.3, sm: 0.5 },
                fontSize: { xs: '0.9rem', sm: '1rem' },
              }}
            >
              {day}
            </Typography>
            <Typography variant="caption" sx={{ color: status.color, fontWeight: 600, fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
              {status.label}
            </Typography>
            {dayData.holidays && dayData.holidays.length > 0 && (
              <BeachAccess sx={{ fontSize: { xs: 10, sm: 14 }, color: '#D4AF37', display: 'block', mx: 'auto', mt: { xs: 0.3, sm: 0.5 } }} />
            )}
          </Card>
        </Grid>
      );
    }

    return (
      <Grid container spacing={{ xs: 0.3, sm: 1 }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <Grid item xs={12/7} key={day}>
            <Typography
              sx={{
                textAlign: 'center',
                fontWeight: 700,
                color: day === 'Sun' || day === 'Sat' ? '#D4AF37' : '#B4C4A8',
                py: { xs: 0.5, sm: 1 },
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
              }}
            >
              {day}
            </Typography>
          </Grid>
        ))}
        {emptyCells}
        {dayCells}
      </Grid>
    );
  };

  const renderDayDetails = () => {
    if (!selectedDay || !calendarData || !calendarData.days) return null;

    const dayData = calendarData.days.find(d => d.date === selectedDay);
    if (!dayData) return null;

    return (
      <Card sx={{ mt: 3, bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#D4AF37', fontWeight: 600 }}>
              {selectedDay} - {dayData.day}
            </Typography>
            <IconButton 
              onClick={() => setSelectedDay(null)} 
              sx={{ color: '#D4AF37', '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
            >
              <Close />
            </IconButton>
          </Box>

          {dayData.holidays && dayData.holidays.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#D4AF37' }}>
                <BeachAccess sx={{ color: '#D4AF37' }} /> Holidays
              </Typography>
              {dayData.holidays.map((holiday, index) => (
                <Chip
                  key={index}
                  label={`${holiday.name} (${holiday.day})`}
                  sx={{ mr: 1, mb: 1, bgcolor: 'rgba(212,175,55,0.2)', color: '#D4AF37', border: '1px solid #D4AF37' }}
                />
              ))}
            </Box>
          )}

          {dayData.leaves && dayData.leaves.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#FFB74D' }}>
                <EventNote sx={{ color: '#FFB74D' }} /> Leaves
              </Typography>
              {dayData.leaves.map((leave, index) => (
                <Chip
                  key={index}
                  label={`${leave.employee_name} - ${leave.leave_type}`}
                  sx={{ mr: 1, mb: 1, bgcolor: 'rgba(255,152,0,0.2)', color: '#FFB74D', border: '1px solid #FFB74D' }}
                />
              ))}
            </Box>
          )}

          {dayData.attendance && dayData.attendance.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#81C784' }}>
                <Event sx={{ color: '#81C784' }} /> Attendance
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgb(10, 25, 41)' }}>
                      <TableCell sx={{ color: '#D4AF37', fontWeight: 600 }}>Employee</TableCell>
                      <TableCell sx={{ color: '#D4AF37', fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ color: '#D4AF37', fontWeight: 600 }}>Login</TableCell>
                      <TableCell sx={{ color: '#D4AF37', fontWeight: 600 }}>Logout</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dayData.attendance.map((att, index) => (
                      <TableRow key={index} hover sx={{ '&:hover': { bgcolor: 'rgba(212,175,55,0.05)' } }}>
                        <TableCell sx={{ color: '#FFFFFF' }}>{att.employee_name}</TableCell>
                         <TableCell>
                           <Chip
                             label={att.status}
                             size="small"
                             sx={{
                               bgcolor: att.status === 'Present' ? 'rgba(76, 175, 80, 0.2)' :
                                      att.status === 'Weekend' ? 'rgba(158, 158, 158, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                               color: att.status === 'Present' ? '#81C784' :
                                     att.status === 'Weekend' ? '#9E9E9E' : '#EF9A9A',
                               border: `1px solid ${att.status === 'Present' ? '#81C784' :
                                                    att.status === 'Weekend' ? '#9E9E9E' : '#EF9A9A'}`
                             }}
                           />
                         </TableCell>
                        <TableCell sx={{ color: '#B4C4A8' }}>{formatTimeToTimezone(att.login_time)}</TableCell>
                        <TableCell sx={{ color: '#B4C4A8' }}>{formatTimeToTimezone(att.logout_time)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {(!dayData.holidays || dayData.holidays.length === 0) &&
           (!dayData.leaves || dayData.leaves.length === 0) &&
           (!dayData.attendance || dayData.attendance.length === 0) && (
            <Typography sx={{ color: '#B4C4A8' }}>No events for this day</Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ bgcolor: 'rgb(10, 25, 41)', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography   variant="h4"
              sx={{
                fontWeight: 700,
                color: '#D4AF37',
                textAlign: 'center',
                width: '100%',
              }}
            >               
            Attendance Calendar
            </Typography>
          {isEmployee && (
            <Button 
              variant="contained" 
              startIcon={<BeachAccess />}
              onClick={() => setShowHolidayDialog(true)}
              sx={{ 
                bgcolor: '#9C27B0',
                color: '#FFFFFF',
                '&:hover': { bgcolor: '#7B1FA2' }
              }}
            >
              Select Optional Holidays ({selectedHolidayIds.length}/{holidayLimit})
            </Button>
          )}
        </Box>

        {holidayMessage.text && (
          <Alert 
            severity={holidayMessage.type} 
            sx={{ 
              mb: 2, 
              bgcolor: 'rgb(10, 25, 41)', 
              color: holidayMessage.type === 'success' ? '#81C784' : '#EF9A9A',
              border: `1px solid ${holidayMessage.type === 'success' ? '#81C784' : '#EF9A9A'}`,
              '& .MuiAlert-icon': { color: holidayMessage.type === 'success' ? '#81C784' : '#EF9A9A' }
            }} 
            onClose={() => setHolidayMessage({ type: '', text: '' })}
          >
            {holidayMessage.text}
          </Alert>
        )}

        <Card sx={{ mb: 3, bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#D4AF37' }}>Year</InputLabel>
                  <Select
                    value={selectedYear}
                    label="Year"
                    onChange={(e) => setSelectedYear(e.target.value)}
                    sx={{ color: '#D4AF37', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } }}
                  >
                    {[2023, 2024, 2025, 2026].map((year) => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#D4AF37' }}>Month</InputLabel>
                  <Select
                    value={selectedMonth}
                    label="Month"
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    sx={{ color: '#D4AF37', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } }}
                  >
                    {[
                      'January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'
                    ].map((month, index) => (
                      <MenuItem key={index} value={index}>{month}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<ChevronLeft />}
                    onClick={goToPrevMonth}
                    sx={{ borderColor: '#D4AF37', color: '#D4AF37', '&:hover': { borderColor: '#C19B2E', bgcolor: 'rgba(212,175,55,0.1)' } }}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outlined"
                    endIcon={<ChevronRight />}
                    onClick={goToNextMonth}
                    sx={{ borderColor: '#D4AF37', color: '#D4AF37', '&:hover': { borderColor: '#C19B2E', bgcolor: 'rgba(212,175,55,0.1)' } }}
                  >
                    Next
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Legend */}
         <Card 
            sx={{ 
              mt: 3,
              mb: 3,
              bgcolor: 'rgb(10, 25, 41)',
              border: '1px solid #D4AF37',
              borderRadius: 3
            }}
          >
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, color: '#D4AF37', fontWeight: 600 }}>
              Calendar Legend
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#81C784', borderRadius: 1 }} />
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Present</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#EF9A9A', borderRadius: 1 }} />
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Absent</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#9E9E9E', borderRadius: 1 }} />
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Weekend Off</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BeachAccess sx={{ color: '#D4AF37', fontSize: 16 }} />
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Holiday</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventNote sx={{ color: '#FFB74D', fontSize: 16 }} />
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Leave</Typography>
                </Box>
              </Grid>
              {weekendOff && (
                <>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: '#EF9A9A' }}>Red</Typography>
                      <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Weekend Off</Typography>
                    </Box>
                  </Grid>
                  {firstSaturdayWork && (
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: '#81C784' }}>Green</Typography>
                        <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Working Saturday</Typography>
                      </Box>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        {loading || !timezoneLoaded ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#D4AF37' }} />
          </Box>
        ) : (
          <>
            <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: '#D4AF37', fontWeight: 600 }}>
                      {getMonthName(selectedMonth)} {selectedYear}
                    </Typography>
                    {renderCalendar()}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            {renderDayDetails()}
          </>
        )}

        {/* Optional Holidays Selection Dialog */}
        <Dialog 
          open={showHolidayDialog} 
          onClose={() => setShowHolidayDialog(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgb(10, 25, 41)',
              border: '2px solid #D4AF37',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
            Select Optional Holidays ({selectedHolidayIds.length}/{holidayLimit})
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, color: '#B4C4A8' }}>
              You can select up to {holidayLimit} optional holidays for {selectedYear}. Your selections will be sent for approval.
            </Typography>
            {optionalHolidays.length === 0 ? (
              <Typography sx={{ color: '#B4C4A8' }}>No optional holidays available for {selectedYear}</Typography>
            ) : (
              <List>
                {optionalHolidays.map((holiday) => (
                  <ListItem 
                    key={holiday.id} 
                    button
                    onClick={() => handleSelectHoliday(holiday.id)}
                    sx={{ 
                      border: '1px solid rgba(212,175,55,0.3)', 
                      borderRadius: 2, 
                      mb: 1,
                      bgcolor: selectedHolidayIds.includes(holiday.id) ? 'rgba(156, 39, 176, 0.15)' : 'transparent',
                      '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' }
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedHolidayIds.includes(holiday.id)}
                        sx={{ color: '#D4AF37', '&.Mui-checked': { color: '#D4AF37' } }}
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary={<Typography sx={{ color: '#FFFFFF' }}>{holiday.holiday_name}</Typography>}
                      secondary={<Typography sx={{ color: '#B4C4A8' }}>{holiday.holiday_date} ({holiday.day})</Typography>}
                    />
                    {!holiday.is_optional && (
                      <Chip label="Mandatory" size="small" sx={{ bgcolor: 'rgba(212,175,55,0.2)', color: '#D4AF37' }} />
                    )}
                    {holiday.selectionStatus === 'approved' && (
                      <Chip label="Approved" size="small" sx={{ bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#81C784' }} />
                    )}
                    {holiday.selectionStatus === 'pending' && (
                      <Chip label="Pending" size="small" sx={{ bgcolor: 'rgba(255, 152, 0, 0.2)', color: '#FFB74D' }} />
                    )}
                    {holiday.selectionStatus === 'rejected' && (
                      <Chip label="Rejected" size="small" sx={{ bgcolor: 'rgba(244, 67, 54, 0.2)', color: '#EF9A9A' }} />
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.3)' }}>
            <Button 
              onClick={() => setShowHolidayDialog(false)} 
              sx={{ color: '#B4C4A8', '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSaveHolidays}
              disabled={savingHolidays}
              sx={{ 
                bgcolor: '#9C27B0',
                color: '#FFFFFF',
                '&:hover': { bgcolor: '#7B1FA2' }
              }}
            >
              {savingHolidays ? 'Saving...' : 'Save Selection'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Calendar;